import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { mfaVerify } from '@/lib/api';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_TEXT_LINK } from './authStyles';
// Recovery codes are lower-case and verified case-sensitively: the phone
// keyboards' sentence capitalisation and autocorrect ('Be4f1-…') would otherwise
// burn a lockout attempt on a correct code.
const RECOVERY_CODE_INPUT = {
    autoComplete: 'off',
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
};
// The numeric keypad and SMS/authenticator autofill. No maxLength: the browser
// cuts a paste down to it before the change handler runs, so '123 456' became
// '123 45'; the handler keeps the first six digits instead (TOTP_DIGITS).
const TOTP_INPUT = {
    autoComplete: 'one-time-code',
    inputMode: 'numeric',
    pattern: '[0-9]*',
};
const TOTP_DIGITS = 6;
/**
 * Second step of the two-step login: collects a 6-digit TOTP code (or a
 * single-use recovery code) and exchanges it + the challenge token for a real
 * session via POST /auth/mfa/verify, handing the result to establishSession().
 * Rendered inside the host page's Card by both Login and AcceptInvitation.
 */
export function MfaChallenge({ challengeToken, email, onSuccess, onCancel, rememberMe = false, }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { establishSession } = useAuth();
    const [code, setCode] = useState('');
    const [useRecoveryCode, setUseRecoveryCode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim())
            return;
        setIsSubmitting(true);
        try {
            const session = await mfaVerify({
                challengeToken,
                code: useRecoveryCode ? normalizeRecoveryCode(code) : code.trim(),
                rememberMe,
            });
            establishSession({ email, accessToken: session.accessToken, csrfToken: session.csrfToken });
            onSuccess();
        }
        catch (error) {
            const status = error instanceof ApiError ? error.status : 0;
            const description = status === 423
                ? t('login.mfa.lockedOut')
                : status === 429
                    ? t('login.mfa.tooManyRequests')
                    : status === 401
                        ? t('login.mfa.invalidCode')
                        : t('login.mfa.failed');
            toast({ title: t('login.failedTitle'), description, variant: 'destructive' });
        }
        finally {
            // Reset on every path. On the Login page success unmounts this via the
            // isAuthenticated→Redirect, but AcceptInvitation keeps it mounted across
            // its setLocation, so a stuck spinner needs this explicit reset.
            setIsSubmitting(false);
        }
    };
    const toggleMode = () => {
        setUseRecoveryCode((prev) => !prev);
        setCode('');
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsx("p", { className: "text-muted-foreground text-center text-sm", children: useRecoveryCode ? t('login.mfa.recoverySubtitle') : t('login.mfa.subtitle') }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "mfa-code", children: useRecoveryCode ? t('login.mfa.recoveryCodeLabel') : t('login.mfa.codeLabel') }), _jsx(Input, { id: "mfa-code", value: code, onChange: (e) => setCode(useRecoveryCode
                            ? e.target.value
                            : e.target.value.replace(/\D/g, '').slice(0, TOTP_DIGITS)), disabled: isSubmitting, autoFocus: true, enterKeyHint: "go", "data-testid": "mfa-code-input", ...(useRecoveryCode ? RECOVERY_CODE_INPUT : TOTP_INPUT) })] }), _jsx(Button, { type: "submit", className: "w-full", loading: isSubmitting, loadingText: t('login.mfa.verifying'), disabled: !code.trim(), "data-testid": "mfa-verify-button", children: t('login.mfa.verify') }), _jsxs("div", { className: "flex flex-col items-center gap-2 text-sm", children: [_jsx("button", { type: "button", className: cn(AUTH_TEXT_LINK, 'text-muted-foreground'), onClick: toggleMode, disabled: isSubmitting, children: useRecoveryCode ? t('login.mfa.useTotpCode') : t('login.mfa.useRecoveryCode') }), _jsx("button", { type: "button", className: cn(AUTH_TEXT_LINK, 'text-muted-foreground'), onClick: onCancel, disabled: isSubmitting, children: t('login.mfa.backToLogin') })] })] }));
}
/**
 * Recovery codes are issued lower-case as 'xxxxx-xxxxx'. Drop whitespace a paste
 * or the keyboard may add and fold the case so a capitalised code still matches.
 */
function normalizeRecoveryCode(code) {
    return code.replace(/\s+/g, '').toLowerCase();
}
